import React, { FC, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { Sheet, SheetContent } from '../../../../components/sheet/index';
import { QueryKey } from '../../../../constants/index';
import {
  Stakeholder as StakeholderHook,
  useInvalidateQueries,
  useReactForm,
} from '../../../../hooks/index';
import { PageRoute } from '../../../../types/page.types';
import { Stakeholder } from '../../../../types/stakeholder.types';
import { handleFormInvalid } from '../../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import { ModifyGuideTab } from '../../../modify-guide/modify-guide.types';
import { hasPartiallyTerminated } from './partial-termination.util';
import { TerminateForm } from './terminate-form';
import { FormSchema, formSchema } from './terminate-stakeholder-modal.validation';

export type TerminateStakeholderModalProps = {
  companyId: string;
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpen: boolean;
  setSelectedStakeholderIds: React.Dispatch<React.SetStateAction<string[]>>;
  stakeholders?: Stakeholder[];
};

export const TerminateStakeholderModal: FC<TerminateStakeholderModalProps> = ({
  isOpen,
  companyId,
  handleClose,
  invalidateQuery,
  stakeholders,
  setSelectedStakeholderIds,
}) => {
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getFieldState,
    resetField,
    setError,
    clearErrors,
  } = useReactForm({
    schema: formSchema,
    mode: 'onChange',
  });

  const { terminate } = StakeholderHook.useTerminate();

  /**
   * Termination settles the stakeholder's reverse-vesting rows: lapsed shares are repurchased and
   * come off the holder's balance the way a buyback takes them off, so issued share capital and
   * every ownership figure derived from it move. The page's own `invalidateQuery` only covers the
   * stakeholder lists, and the backend's post-commit `StakeholdersSideEffectsService.terminate`
   * clears Redis but cannot reach this client's React Query cache — without these keys the app
   * keeps painting pre-termination numbers until an unrelated mutation happens to invalidate them.
   */
  const { invalidateQuery: invalidateTerminationDerivedQueries } = useInvalidateQueries(
    QueryKey.GET_STAKEHOLDER_REVERSE_VESTING,
    QueryKey.GET_ISSUED_SHARES,
    QueryKey.GET_SNAPSHOT,
    QueryKey.GET_SNAPSHOTS,
    QueryKey.GET_STATS,
  );

  const handleCloseModal = useCallback(() => {
    reset();
    handleClose();
    setSelectedStakeholderIds([]);
  }, [reset, handleClose, setSelectedStakeholderIds]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const { terminateForm } = data;
      const stakeholders = terminateForm.stakeholderItems.map(
        ({
          id,
          terminationCause,
          terminationDate,
          grantItems,
          lastVestingDate,
          isLastVestingDateManual,
          reverseVestingItems,
        }) => ({
          id,
          terminationDate: toBackendDateFormat(terminationDate),
          terminationCause,
          lastVestingDate: lastVestingDate ? toBackendDateFormat(lastVestingDate) : undefined,
          isLastVestingDateManual: Boolean(isLastVestingDateManual),
          // Only the operator's two inputs travel: the contractual figures in the form are there
          // for the row's own arithmetic, and the backend re-derives them from the schedule.
          reverseVestingItems: reverseVestingItems?.map(
            ({ id, acceleratedShares, repurchasePrice }) => ({
              id,
              acceleratedShares: acceleratedShares ?? 0,
              repurchasePrice,
            }),
          ),
          grantItems:
            grantItems?.map(
              ({ id, finalExerciseDate, grantItem, vestedSharesCount, exercisedCount }) => ({
                id,
                finalExerciseDate:
                  finalExerciseDate?.getTime() === 0
                    ? undefined
                    : finalExerciseDate
                      ? toBackendDateFormat(finalExerciseDate)
                      : undefined,
                planType: grantItem.plan.type,
                numbersOfGrants: grantItem?.numbersOfGrants || null,
                vestedSharesCount: vestedSharesCount || null,
                unvestedSharesCount: grantItem.numbersOfGrants
                  ? grantItem.numbersOfGrants - (vestedSharesCount || 0)
                  : null,
                exercisedCount: exercisedCount || null,
                unexercisedSharesCount: vestedSharesCount
                  ? vestedSharesCount - (exercisedCount || 0)
                  : null,
              }),
            ) || undefined,
        }),
      );

      terminate(
        {
          companyId,
          data: { stakeholders, sendEmails: !!terminateForm.isSendEmails },
        },
        {
          onSuccess: () => {
            toast.success(
              `${terminateForm.stakeholderItems.length} stakeholder${terminateForm.stakeholderItems.length > 1 ? 's' : ''} terminated`,
            );
            invalidateQuery();
            invalidateTerminationDerivedQueries();
            handleCloseModal();
          },
          /**
           * A batch commits one stakeholder per transaction, so a payload rejected partway through
           * leaves the ones before it terminated. `useAppMutation` has already toasted the reason;
           * what is left is making the client's view match the server's.
           *
           * Invalidation runs on every rejection, not only the partial one: a failure that reaches
           * here after any commit — including a connection drop the backend could not classify —
           * leaves the cached numbers wrong, and a refetch on an untouched company costs nothing.
           *
           * The modal closes only when the server says something committed, because that is the
           * case where the form is no longer safe to resubmit: terminating an already-terminated
           * stakeholder is not idempotent (the announcement email and notification fire again).
           * Closing drops the selection, so the retry is re-picked from a refreshed list. A
           * rejection that committed nothing keeps the modal open so the operator can fix the
           * offending field in place.
           */
          onError: (error) => {
            invalidateQuery();
            invalidateTerminationDerivedQueries();

            if (hasPartiallyTerminated(error)) handleCloseModal();
          },
        },
      );
    },
    [companyId, handleCloseModal, invalidateQuery, invalidateTerminationDerivedQueries, terminate],
  );

  const handleTerminate = useCallback(() => {
    handleSubmit(submitHandler, handleFormInvalid)();
  }, [handleSubmit, submitHandler]);

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpen}>
      <SheetContent
        className="h-fit max-h-[800px] w-[90%] max-w-[1400px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="CENTER"
      >
        <div className="flex h-fit w-full flex-col gap-10 rounded-2xl bg-white p-8">
          <div className="flex h-fit w-full items-end justify-between">
            <div className="flex flex-col gap-2">
              <span className="text-xl font-semibold text-gray-700">
                <AppFormattedMessage id="TERMINATE_STAKEHOLDER" />
              </span>
              <span className="text-sm font-[450] text-gray-700">
                <AppFormattedMessage id="FILL_IN_DETAILS" />
              </span>
            </div>
            <Link
              className="flex w-fit cursor-pointer items-center justify-center gap-2 rounded text-sm font-[450] text-brand-700 underline disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-300 disabled:ring-1 disabled:ring-gray-200"
              rel="noopener noreferrer"
              target="_blank"
              to={`${PageRoute.GUIDES}/${ModifyGuideTab.STAKEHOLDERS_TERMINATION}`}
            >
              <AppFormattedMessage id="LEARN_MORE_TERMINATION" />
            </Link>
          </div>
          <div className="flex h-full max-h-[520px] flex-col overflow-y-auto">
            <TerminateForm
              clearErrors={clearErrors}
              companyId={companyId}
              control={control}
              filedState={getFieldState}
              handleCloseModal={handleCloseModal}
              handleTerminate={handleTerminate}
              resetField={resetField}
              setError={setError}
              setFormData={setValue}
              stakeholders={stakeholders}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
